import { create } from 'zustand'
import { api } from '@/lib/api'

export type TVLayout = 'single' | 'dual' | 'quad'

export interface TVTile {
  slot: number
  channelId: string
  channelName: string
  streamUrl: string
  iconUrl?: string
}

export type CastState = 'idle' | 'connecting' | 'connected' | 'error'

interface CastSession {
  id: string
  playbackUrl: string
  playbackToken: string
}

interface TVSceneState {
  // Scene state
  layout: TVLayout
  tiles: TVTile[]
  activeAudioSlot: number

  // Cast state
  castState: CastState
  castSession: CastSession | null
  castError: string | null
  castDeviceName: string | null

  // Actions
  setLayout: (layout: TVLayout) => void
  addTile: (tile: TVTile) => void
  removeTile: (slot: number) => void
  setActiveAudio: (slot: number) => void
  clearTiles: () => void

  // Cast actions
  startCastSession: () => Promise<void>
  updateCastSession: () => Promise<void>
  stopCastSession: () => Promise<void>
  setCastState: (state: CastState) => void
  setCastDeviceName: (name: string | null) => void
  setCastError: (error: string | null) => void

  // Helpers
  getMaxSlots: () => number
  hasTile: (channelId: string) => boolean
}

export const useTVScene = create<TVSceneState>((set, get) => ({
  // Initial state
  layout: 'quad',
  tiles: [],
  activeAudioSlot: 0,
  castState: 'idle',
  castSession: null,
  castError: null,
  castDeviceName: null,

  // Layout management
  setLayout: (layout: TVLayout) => {
    const maxSlots = layout === 'single' ? 1 : layout === 'dual' ? 2 : 4
    const currentTiles = get().tiles

    // Remove tiles that exceed the new layout limit
    const filteredTiles = currentTiles
      .filter(t => t.slot < maxSlots)
      .sort((a, b) => a.slot - b.slot)

    // Adjust active audio slot if needed
    let activeAudioSlot = get().activeAudioSlot
    if (activeAudioSlot >= maxSlots) {
      activeAudioSlot = 0
    }

    set({ layout, tiles: filteredTiles, activeAudioSlot })

    // Update cast session if active
    if (get().castSession) {
      get().updateCastSession()
    }
  },

  // Tile management
  addTile: (tile: TVTile) => {
    const { tiles, getMaxSlots } = get()
    const maxSlots = getMaxSlots()

    // Check if channel is already playing
    if (tiles.some(t => t.channelId === tile.channelId)) {
      return
    }

    // Check if slot is valid
    if (tile.slot >= maxSlots) {
      return
    }

    // Remove any existing tile in this slot
    const newTiles = tiles.filter(t => t.slot !== tile.slot)
    newTiles.push(tile)

    set({ tiles: newTiles })

    // Update cast session if active
    if (get().castSession) {
      get().updateCastSession()
    }
  },

  removeTile: (slot: number) => {
    const newTiles = get().tiles.filter(t => t.slot !== slot)
    set({ tiles: newTiles })

    // Update cast session if active
    if (get().castSession) {
      get().updateCastSession()
    }
  },

  setActiveAudio: (slot: number) => {
    const maxSlots = get().getMaxSlots()
    if (slot >= 0 && slot < maxSlots) {
      set({ activeAudioSlot: slot })

      // Update cast session if active
      if (get().castSession) {
        get().updateCastSession()
      }
    }
  },

  clearTiles: () => {
    set({ tiles: [], activeAudioSlot: 0 })
  },

  // Cast session management
  startCastSession: async () => {
    const { layout, tiles, activeAudioSlot } = get()

    if (tiles.length === 0) {
      set({ castError: 'No channels selected to cast' })
      return
    }

    set({ castState: 'connecting', castError: null })

    try {
      // Convert layout to API format
      const layoutMap: Record<TVLayout, string> = {
        single: 'single',
        dual: 'dual',
        quad: 'quad',
      }

      // Build tiles array for API
      const apiTiles = tiles.map(t => ({
        slot: t.slot,
        stream_id: t.channelId,
      }))

      const response = await api.createCastSession({
        layout: layoutMap[layout],
        tiles: apiTiles,
        active_audio_slot: activeAudioSlot,
      })

      set({
        castSession: {
          id: response.id,
          playbackUrl: response.playback_url,
          playbackToken: response.playback_token,
        },
        castState: 'connected',
      })
    } catch (error) {
      console.error('[TVScene] Failed to create cast session:', error)
      set({
        castState: 'error',
        castError: error instanceof Error ? error.message : 'Failed to start casting',
      })
    }
  },

  updateCastSession: async () => {
    const { castSession, layout, tiles, activeAudioSlot } = get()

    if (!castSession) {
      return
    }

    try {
      const layoutMap: Record<TVLayout, string> = {
        single: 'single',
        dual: 'dual',
        quad: 'quad',
      }

      const apiTiles = tiles.map(t => ({
        slot: t.slot,
        stream_id: t.channelId,
      }))

      await api.updateCastSession(castSession.id, {
        layout: layoutMap[layout],
        tiles: apiTiles,
        active_audio_slot: activeAudioSlot,
      })
    } catch (error) {
      console.error('[TVScene] Failed to update cast session:', error)
      set({
        castError: error instanceof Error ? error.message : 'Failed to update cast',
      })
    }
  },

  stopCastSession: async () => {
    const { castSession } = get()

    if (castSession) {
      try {
        await api.deleteCastSession(castSession.id)
      } catch (error) {
        console.error('[TVScene] Failed to stop cast session:', error)
      }
    }

    set({
      castSession: null,
      castState: 'idle',
      castDeviceName: null,
      castError: null,
    })
  },

  setCastState: (state: CastState) => set({ castState: state }),
  setCastDeviceName: (name: string | null) => set({ castDeviceName: name }),
  setCastError: (error: string | null) => set({ castError: error }),

  // Helpers
  getMaxSlots: () => {
    const { layout } = get()
    return layout === 'single' ? 1 : layout === 'dual' ? 2 : 4
  },

  hasTile: (channelId: string) => {
    return get().tiles.some(t => t.channelId === channelId)
  },
}))
